// @flow
import * as React from 'react';
import {
  Route,
  Redirect,
  withRouter,
  type ContextRouter,
} from 'react-router-dom';
import ClientProvider from 'containers/client/ClientProvider';
import Switch from './__components__/Switch';
import RunAsBanner from './__components__/RunAsBanner';
import { connect } from 'react-redux';

import Moderation from 'pages/moderation';
import Reviews from 'pages/reviews';
import QA from 'pages/qa';
import SocialCollection from 'pages/socialcollection';

import { AuthState } from 'ducks/authReducer';

import CheckAuthentication from 'pages/__components__/CheckAuthentication';
import CheckAuthorization from 'pages/__components__/CheckAuthorization';
import AppReady from 'pages/__components__/AppReady';
import LoadAppConfig from 'pages/__components__/LoadAppConfig';
import { UserSatisfaction } from 'pages/__components__/UserSatisfaction';
import RedirectNotice from 'components/RedirectNotice';
import { AuthContext, redirectToAuthService } from 'ducks/auth-context';
import { logout } from 'ducks/authActions';
import { WyngSingleSignOn } from '../containers/socialcollection/WyngSingleSignOn';
import { UserTranslation } from './__components__/UserTranslation';

const ModerationPathName = 'moderation';
const OldModerationPathName = 'content';
const ReviewsPathName = 'reviews';
const QAPathName = 'q&a';
const SocialCollectionPathName = 'socialcollection';
const SocialCollectionSSOPathName = 'socialcollection/sso/saml2'; // IDP SAML Request proxy

const USER_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const USER_IDLE_TIMEOUT_CHECK_INTERVAL_MS = Math.floor(
  USER_IDLE_TIMEOUT_MS / 4
);

type Props = { auth: AuthState, logout: () => void } & ContextRouter;
type State = { userInfo: ?UserInfo };

export class Main extends React.PureComponent<Props, State> {
  state = {
    userInfo: null,
  };

  checkUserIdleTimeIntervelId: IntervalID;
  userIdleStartTime: ?number;

  checkUserIdleTime = () => {
    let userIdleStartTime = this.userIdleStartTime;
    if (typeof userIdleStartTime === 'number') {
      if (Date.now() - userIdleStartTime >= USER_IDLE_TIMEOUT_MS) {
        this.props.logout();
        redirectToAuthService();
      }
    }
  };

  handlePageVisibility = (e: Object) => {
    if (e.target.visibilityState === 'visible') {
      this.userIdleStartTime = null;
    } else {
      this.userIdleStartTime = Date.now();
    }
  };

  static getDerivedStateFromProps(nextProps: Props) {
    return {
      userInfo: AuthState.getUserInfo(nextProps.auth),
    };
  }

  componentDidMount() {
    document.addEventListener(
      'visibilitychange',
      this.handlePageVisibility,
      false
    );

    this.checkUserIdleTimeIntervelId = setInterval(
      this.checkUserIdleTime,
      USER_IDLE_TIMEOUT_CHECK_INTERVAL_MS
    );
  }

  componentWillUnmount() {
    document.removeEventListener(
      'visibilitychange',
      this.handlePageVisibility,
      false
    );

    clearInterval(this.checkUserIdleTimeIntervelId);
  }

  render() {
    const { match, auth } = this.props;
    const { userInfo } = this.state;
    const access = userInfo != null ? userInfo.access : {};
    const redirectDestination = getLandingPage(match.url, access);
    const isPowerReviewsUserInRunAsMode = auth.isRunAsMode();

    return (
      <CheckAuthentication>
        <CheckAuthorization authorized={access.app === true}>
          <AppReady>
            {isPowerReviewsUserInRunAsMode ? <RunAsBanner /> : null}
            <AuthContext.Provider value={auth}>
              <LoadAppConfig>
                <ClientProvider>
                  <Switch>
                    <Redirect exact from={match.url} to={redirectDestination} />
                    {access.moderate === true && (
                      <Route
                        path={`${match.url}${ModerationPathName}`}
                        render={(routeProps: ContextRouter) => (
                          <Moderation {...userInfo} {...routeProps} />
                        )}
                      />
                    )}
                    {access.moderate === true && (
                      <Route
                        path={`${match.url}${OldModerationPathName}`}
                        render={(routeProps: ContextRouter) => (
                          <RedirectNotice delay={10}>
                            {() => (
                              <Redirect
                                to={routeProps.location.pathname.replace(
                                  '/' + OldModerationPathName,
                                  '/' + ModerationPathName
                                )}
                              />
                            )}
                          </RedirectNotice>
                        )}
                      />
                    )}
                    {access.reviews === true && (
                      <Route
                        path={`${match.url}${ReviewsPathName}`}
                        render={(routeProps: ContextRouter) => (
                          <Reviews {...userInfo} {...routeProps} />
                        )}
                      />
                    )}
                    {access.qa === true && (
                      <Route
                        path={`${match.url}${QAPathName}`}
                        render={(routeProps: ContextRouter) => (
                          <QA {...userInfo} {...routeProps} />
                        )}
                      />
                    )}
                    <Route
                      path={`${match.url}${SocialCollectionSSOPathName}`}
                      render={(routeProps: ContextRouter) => (
                        <WyngSingleSignOn
                          queryString={routeProps.location.search}
                          token={auth.token}
                        />
                      )}
                    />
                    <Route
                      path={`${match.url}${SocialCollectionPathName}`}
                      render={(routeProps: ContextRouter) => (
                        <SocialCollection {...userInfo} {...routeProps} />
                      )}
                    />
                  </Switch>
                  <UserSatisfaction
                    username={auth.name}
                    email={auth.email}
                    page={this.props.location.pathname}
                    internal={
                      auth.role.internal || isPowerReviewsUserInRunAsMode
                    }
                  />
                  <UserTranslation />
                </ClientProvider>
              </LoadAppConfig>
            </AuthContext.Provider>
          </AppReady>
        </CheckAuthorization>
      </CheckAuthentication>
    );
  }
}

function getLandingPage(matchUrl: string, access: *): string {
  if (access.reviews) {
    return `${matchUrl}${ReviewsPathName}`;
  }

  if (access.qa) {
    return `${matchUrl}${QAPathName}`;
  }

  return `${matchUrl}${ModerationPathName}`;
}

const mapStateToProps = ({
  auth,
}: {
  auth: AuthState,
}): {| auth: AuthState |} => ({
  auth,
});

export default withRouter(connect(mapStateToProps, { logout })(Main));



// WEBPACK FOOTER //
// ./src/pages/index.js